// components/listsShared.js
// Reusable helpers for list-like pages (Price, Promotion, Company, Acquisition, …)

import BackOfficeConfig from '../Config';

/* ---------- dates & time ---------- */
export const todayISO = () => new Date().toISOString().slice(0, 10);

export const nowLocalDT = () => {
  const dt = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
};

/* ---------- CSV & downloads ---------- */
export const csvCell = (v) => {
  const s = String(v == null ? '' : v);
  return s.includes(',') || s.includes('\n') || s.includes('"')
    ? '"' + s.replaceAll('"', '""') + '"'
    : s;
};
export const downloadBlob = (content, filename, mime) => {
  const isCsv = (mime || '').toLowerCase().includes('csv');
  const blob = new Blob([isCsv ? '\uFEFF' + content : content], {
    type: mime || 'text/csv;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

/* ---------- currency helpers ---------- */
export const toCurrencyCode = (val) => {
  if (val === null || val === undefined || val === '') return val;
  const n = Number(val);
  if (!Number.isNaN(n)) return n;
  try {
    const entries = Object.entries(BackOfficeConfig.CURRENCIES || {}); // {"0":"RON",...}
    const map = entries.reduce((acc, [k, label]) => { acc[String(label).toUpperCase()] = Number(k); return acc; }, {});
    const code = map[String(val).toUpperCase()];
    return (code !== undefined ? code : val);
  } catch { return val; }
};

export const toCurrencyName = (val) => {
  if (typeof val === 'string' && !/^\d+$/.test(val)) return val;
  try {
    const map = BackOfficeConfig.CURRENCIES || {};
    const key = String(val);
    return map[key] || key;
  } catch { return String(val); }
};

/* ---------- track grouping (Company × Location × Currency × VAT) ---------- */
export const latestKey = (x) =>
  `${x?.company?.iref || x?.company}__${x?.location?.iref || x?.location}__${x?.currency}__${x?.withVAT ? 'VAT' : 'NOVAT'}`;

export function groupLatest(arr) {
  const map = new Map();
  (arr || []).forEach((x) => {
    const k = latestKey(x);
    const m = map.get(k);
    if (!m || Number(x.version) > Number(m.version)) map.set(k, x);
  });
  return Array.from(map.values());
}

export function safeCount(maybe) {
  return Array.isArray(maybe) ? maybe.length : (maybe ? Object.keys(maybe).length : 0);
}

export function enrichCounts(pl) {
  return {
    ...pl,
    _prodCount: safeCount(pl?.productPrice_list),
    _comboCount: safeCount(pl?.productCombo_list),
  };
}

/* ---------- overlap window helpers ---------- */
export const toDate = (s) => (s ? new Date(`${s}T00:00:00`) : null);

export const rangesOverlap = (aStart, aEnd, bStart, bEnd) => {
  if (!aStart || !aEnd || !bStart || !bEnd) return false;
  return aStart <= bEnd && bStart <= aEnd;
};

export const filterByOverlap = (rows, fromISO, toISO) => {
  const f = toDate(fromISO), t = toDate(toISO);
  if (!f || !t) return rows;
  return (rows || []).filter((v) =>
    rangesOverlap(
      toDate(v.dateFrom || v.date_from),
      toDate(v.dateTo || v.date_to),
      f, t
    )
  );
};

/* ---------- keyboard shortcuts guard ---------- */
export const isUserTyping = () => {
  try {
    const ae = document.activeElement;
    const tag = ae && ae.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA') return true;
    if (ae?.getAttribute?.('contenteditable') === 'true') return true;
    if (ae?.closest?.('[role="combobox"],[role="textbox"]')) return true;
  } catch {}
  return false;
};

/* ---------- PRODUCTS / COMBOS helpers ---------- */
export const productsOf = (detail) => {
  const raw = detail?.productPrice_list || [];
  return Array.isArray(raw) ? raw : Object.values(raw || {});
};
export const combosOf = (detail) => {
  const raw = detail?.productCombo_list || [];
  return Array.isArray(raw) ? raw : Object.values(raw || {});
};
export const makeProdKey = (it, idx) => {
  const prod = it.product || it.productRef || it;
  return it.iref || prod?.iref || prod?.sku || `prod_${idx}`;
};
export const makeComboKey = (it, idx) => {
  const comb = it.productCombo || it.comboRef || it;
  return it.iref || comb?.iref || comb?.sku || `combo_${idx}`;
};
export const stableProdKey = (row) => {
  const id =
    row?.product?.iref ?? row?.productRef?.iref ??
    row?.product ?? row?.product_id ?? row?.productID ?? row?.productIref ??
    row?.product_iref ?? row?.p_id ?? row?.p_iref ?? row?.sku ?? row?.code;
  return String(id ?? '');
};
export const stableComboKey = (row) => {
  const id =
    row?.productCombo?.iref ?? row?.comboRef?.iref ??
    row?.productCombo ?? row?.combo_id ?? row?.comboID ?? row?.comboIref ??
    row?.combo_iref ?? row?.c_id ?? row?.c_iref ?? row?.sku ?? row?.code;
  return String(id ?? '');
};

/* ---------- generic query for preloaded pickers ---------- */
export const filterByQuery = (arr, q, limit = 50) => {
  const src = Array.isArray(arr) ? arr : [];
  if (!q) return src.slice(0, limit);
  const qq = String(q).toLowerCase();
  return src.filter(o =>
    (o.name && o.name.toLowerCase().includes(qq)) ||
    (o.sku && String(o.sku).toLowerCase().includes(qq)) ||
    (o.iref && String(o.iref).toLowerCase().includes(qq))
  ).slice(0, limit);
};

export const normalizeIds = (arr) => {
  if (!Array.isArray(arr)) return [];
  const ids = arr.map(x => {
    if (x === '__ALL__') return '__ALL__';
    if (x && typeof x === 'object') return x.iref || x.id || x.value || '';
    return String(x || '');
  }).filter(Boolean);
  // Dedupe; if ALL is chosen, keep only ALL
  return ids.includes('__ALL__') ? ['__ALL__'] : Array.from(new Set(ids));
};

// ---------- CSV: single price list, with details ----------
export function exportDetailCSV(detail, helpers = {}) {
  if (!detail) return;
  const {
    // prefer instance-aware helpers if caller provides them
    productsOf,
    combosOf,
    normalizeProductItem,
    normalizeComboItem,
    // fallbacks from this module
    toCurrencyName: _toCurrencyName = toCurrencyName,
    csvCell: _csvCell = csvCell,
    downloadBlob: _downloadBlob = downloadBlob,
    todayISO: _todayISO = todayISO,
  } = helpers;

  const _products = (d) => (productsOf ? productsOf(d) : productsOf_default(d));
  const _combos   = (d) => (combosOf ? combosOf(d) : combosOf_default(d));
  const _normP    = (it, d, i) => (normalizeProductItem ? normalizeProductItem(it, d, i) : it);
  const _normC    = (it, d, i) => (normalizeComboItem ? normalizeComboItem(it, d, i) : it);

  const rows = [];

  // multi-line meta header
  [
    ['Price List'],
    ['Location', (detail.location?.name || detail.location || '')],
    ['Version', String(detail.version || '')],
    ['Currency', _toCurrencyName(detail.currency)],
    ['VAT', detail.withVAT ? 'With VAT' : 'No VAT'],
    ['From', detail.dateFrom || ''],
    ['To', detail.dateTo || '∞'],
    ['Description', detail.description || '']
  ].forEach(line => rows.push(line.map(_csvCell).join(',')));

  rows.push('');

  // richer columns
  rows.push(['Type','Iref','Name','SKU','Unit','Class','Category','Price','Currency','Active','IsForSale','Status'].join(','));

  (_products(detail) || []).forEach((it, idx) => {
    const item = _normP(it, detail, idx) || {};
    rows.push([
      'Product',
      item.product?.iref || item.productRef?.iref || '',
      item.name || '',
      item.sku || '',
      (item.unitType?.name || item.unitType || ''),
      (item.pCategory?.name || ''),
      (item.pSubCategory?.name || ''),
      (item.price != null ? item.price : ''),
      _toCurrencyName(item.currency || detail.currency),
      item.isActive ? '1' : '0',
      item.isForSale ? '1' : '0',
      item.status || ''
    ].map(_csvCell).join(','));
  });

  (_combos(detail) || []).forEach((it, idx) => {
    const item = _normC(it, detail, idx) || {};
    rows.push([
      'Combo',
      item.productCombo?.iref || item.comboRef?.iref || '',
      item.name || '',
      item.sku || '',
      '', '', '',
      (item.price != null ? item.price : ''),
      _toCurrencyName(item.currency || detail.currency),
      item.isActive ? '1' : '0',
      item.isForSale ? '1' : '0',
      item.status || ''
    ].map(_csvCell).join(','));
  });

  const csv = rows.join('\n');
  const fname = `pricelist_${(detail.location?.name||detail.location)}_v${detail.version}.csv`;
  _downloadBlob(csv, fname, 'text/csv;charset=utf-8');
}

// ---------- CSV: many price lists (summary OR detailed) ----------
export function exportLatestCSV(lists, helpers = {}, options = {}) {
  const {
    withDetails = false
  } = options;

  const {
    // optional instance-aware helpers
    getDetailByIref,
    hydrateDetailWithCatalogs,
    productsOf,
    combosOf,
    normalizeProductItem,
    normalizeComboItem,
    // fallbacks from this module
    toCurrencyName: _toCurrencyName = toCurrencyName,
    csvCell: _csvCell = csvCell,
    downloadBlob: _downloadBlob = downloadBlob,
    todayISO: _todayISO = todayISO,
  } = helpers;

  const _products = (d) => (productsOf ? productsOf(d) : productsOf_default(d));
  const _combos   = (d) => (combosOf ? combosOf(d) : combosOf_default(d));
  const _normP    = (it, d, i) => (normalizeProductItem ? normalizeProductItem(it, d, i) : it);
  const _normC    = (it, d, i) => (normalizeComboItem ? normalizeComboItem(it, d, i) : it);
  const _hydrate  = (d) => (hydrateDetailWithCatalogs ? hydrateDetailWithCatalogs(d) : d);
  const _getDet   = (id) => (getDetailByIref ? getDetailByIref(id) : null);

  if (!withDetails) {
    // summary
    const header = ['location','version','currency','withVAT','dateFrom','dateTo','products','combos'];
    const lines = (lists || []).map((r)=> [
      r.location?.name || r.location,
      r.version,
      r.currency,
      r.withVAT ? 'withVAT' : 'noVAT',
      r.dateFrom || '',
      r.dateTo || '',
      ((r.productCount !== undefined && r.productCount !== null) ? r.productCount : (r._prodCount || 0)),
      ((r.comboCount !== undefined && r.comboCount !== null) ? r.comboCount : (r._comboCount || 0)),
    ].map(_csvCell).join(','));
    const csv = [header.join(','), ...lines].join('\n');
    _downloadBlob(csv, `latest_pricelists_${_todayISO()}.csv`, 'text/csv;charset=utf-8');
    return;
  }

  // detailed: each list gets a meta block + lines
  const rows = [];
  (lists || []).forEach((r, idx) => {
    let det = r;
    const hasItems = !!(det && (det.productPrice_list || det.productCombo_list));
    if (!hasItems && _getDet) {
      const maybe = _getDet(r.iref);
      // guard: if someone handed us a promise, don’t wait here—fallback to thin row
      det = (maybe && typeof maybe.then === 'function') ? r : (maybe || r);
    }
    if (det) det = _hydrate(det);

    [
      [ (det?.company ? 'Company List' : 'Price List') ],
      ['Company', (det?.company?.name || det?.company || '')],
      ['Location', (det?.location?.name || det?.location || '')],
      ['Version', String(det?.version || '')],
      ['Currency', _toCurrencyName(det?.currency)],
      ['VAT', det?.withVAT ? 'With VAT' : 'No VAT'],
      ['From', det?.dateFrom || ''],
      ['To', det?.dateTo || '∞'],
      ['Description', det?.description || '']
    ].forEach(line => rows.push(line.map(_csvCell).join(',')));

    rows.push('');
    rows.push(['Type','Iref','Name','SKU','Unit','Class','Category','Price','Currency','Active','IsForSale','Status'].join(','));

    (_products(det) || []).forEach((raw, i) => {
      const item = _normP(raw, det, i) || raw || {};
      const name = item.name
        || raw.name
        || item.product?.name
        || item.productRef?.name
        || '';
      const sku  = item.sku
        || raw.sku
        || item.product?.sku
        || item.productRef?.sku
        || '';
      const price = (item.price != null)
        ? item.price
        : (raw.price != null ? raw.price : (raw.value != null ? raw.value : ''));
      const unit  = item.unitType?.name || item.unitType || '';
      const cls   = item.pCategory?.name || '';
      const cat   = item.pSubCategory?.name || '';
      const curr  = item.currency || raw.currency || det?.currency;

      rows.push([
        'Product',
        item.product?.iref || item.productRef?.iref || '',
        name,
        sku,
        unit,
        cls,
        cat,
        price,
        _toCurrencyName(curr),
        item.isActive ? '1' : '0',
        item.isForSale ? '1' : '0',
        item.status || ''
      ].map(_csvCell).join(','));
    });

    (_combos(det) || []).forEach((raw, i) => {
      const item = _normC(raw, det, i) || raw || {};
      const name = item.name
        || raw.name
        || item.productCombo?.name
        || item.comboRef?.name
        || '';
      const sku  = item.sku
        || raw.sku
        || item.productCombo?.sku
        || item.comboRef?.sku
        || '';
      const price = (item.price != null)
        ? item.price
        : (raw.price != null ? raw.price : (raw.value != null ? raw.value : ''));
      const curr  = item.currency || raw.currency || det?.currency;

      rows.push([
        'Combo',
        item.productCombo?.iref || item.comboRef?.iref || '',
        name,
        sku,
        '', '', '',
        price,
        _toCurrencyName(curr),
        item.isActive ? '1' : '0',
        item.isForSale ? '1' : '0',
        item.status || ''
      ].map(_csvCell).join(','));
    });
  });

  const csv = rows.join('\n');
  _downloadBlob(csv, `pricelists_detailed_${_todayISO()}.csv`, 'text/csv;charset=utf-8');
}

// internal defaults that call this module’s existing helpers
function productsOf_default(d){ return productsOf(d); }
function combosOf_default(d){ return combosOf(d); }

/* ---------- PROMOTION-specific helpers ---------- */

// Arrays from Promotion detail
export const promoProductsOf = (detail) => {
  const raw = detail?.productPromotion_list || [];
  return Array.isArray(raw) ? raw : Object.values(raw || {});
};
export const promoCombosOf = (detail) => {
  const raw = detail?.comboPromotion_list || [];
  return Array.isArray(raw) ? raw : Object.values(raw || {});
};

// Count _prodCount / _comboCount on promotion lists
export function enrichPromotionCounts(pl) {
  const prod = Array.isArray(pl?.productPromotion_list) ? pl.productPromotion_list
             : (pl?.productPromotion_list ? Object.values(pl.productPromotion_list) : []);
  const comb = Array.isArray(pl?.comboPromotion_list) ? pl.comboPromotion_list
             : (pl?.comboPromotion_list ? Object.values(pl.comboPromotion_list) : []);
  return {
    ...pl,
    _prodCount: prod.length,
    _comboCount: comb.length,
  };
}

// Map promotion "value" -> "price" so UI/CSV code that expects 'price' works
export const normalizePromotionProductItem = (it, parent, idx) => {
  const name = it?.name || it?.product?.name || it?.productRef?.name || '';
  const sku  = it?.sku  || it?.product?.sku  || it?.productRef?.sku  || '';
  return {
    ...it,
    name,
    sku,
    price: (it?.price != null ? it.price : it?.value),
    currency: it?.currency ?? parent?.currency,
  };
};

export const normalizePromotionComboItem = (it, parent, idx) => {
  const name = it?.name || it?.productCombo?.name || it?.comboRef?.name || '';
  const sku  = it?.sku  || it?.productCombo?.sku  || it?.comboRef?.sku  || '';
  return {
    ...it,
    name,
    sku,
    price: (it?.price != null ? it.price : it?.value),
    currency: it?.currency ?? parent?.currency,
  };
};

/* ---------- CSV: single promotion list (detail) ---------- */
export function exportPromotionDetailCSV(detail, helpers = {}) {
  if (!detail) return;
  const {
    toCurrencyName: _toCurrencyName = toCurrencyName,
    csvCell: _csvCell = csvCell,
    downloadBlob: _downloadBlob = downloadBlob,
  } = helpers;

  // Build rows like exportDetailCSV but with Promotion defaults + title
  const rows = [];
  [
    ['Promotion List'],
    ['Location', (detail.location?.name || detail.location || '')],
    ['Version', String(detail.version || '')],
    ['Currency', _toCurrencyName(detail.currency)],
    ['VAT', detail.withVAT ? 'With VAT' : 'No VAT'],
    ['From', detail.dateFrom || ''],
    ['To', detail.dateTo || '∞'],
    ['Description', detail.description || '']
  ].forEach(line => rows.push(line.map(_csvCell).join(',')));

  rows.push('');
  rows.push(['Type','Iref','Name','SKU','Unit','Class','Category','Price','Currency','Active','IsForSale','Status'].join(','));

  (promoProductsOf(detail) || []).forEach((raw, i) => {
    const item = (helpers.normalizeProductItem
      ? helpers.normalizeProductItem(raw, detail, i)
      : normalizePromotionProductItem(raw, detail, i)) || {};
    rows.push([
      'Product',
      item.product?.iref || item.productRef?.iref || '',
      item.name || '',
      item.sku || '',
      (item.unitType?.name || item.unitType || ''),
      (item.pCategory?.name || ''),
      (item.pSubCategory?.name || ''),
      (item.price != null ? item.price : ''),
      _toCurrencyName(item.currency || detail.currency),
      item.isActive ? '1' : '0',
      item.isForSale ? '1' : '0',
      item.status || ''
    ].map(_csvCell).join(','));
  });

  (promoCombosOf(detail) || []).forEach((raw, i) => {
    const item = (helpers.normalizeComboItem
      ? helpers.normalizeComboItem(raw, detail, i)
      : normalizePromotionComboItem(raw, detail, i)) || {};
    rows.push([
      'Combo',
      item.productCombo?.iref || item.comboRef?.iref || '',
      item.name || '',
      item.sku || '',
      '', '', '',
      (item.price != null ? item.price : ''),
      _toCurrencyName(item.currency || detail.currency),
      item.isActive ? '1' : '0',
      item.isForSale ? '1' : '0',
      item.status || ''
    ].map(_csvCell).join(','));
  });

  const csv = rows.join('\n');
  const fname = `promotion_${(detail.location?.name||detail.location)}_v${detail.version}.csv`;
  _downloadBlob(csv, fname, 'text/csv;charset=utf-8');
}

/* ---------- CSV: many promotion lists (summary OR detailed) ---------- */
export function exportPromotionLatestCSV(lists, helpers = {}, options = {}) {
  const { withDetails = false } = options;

  const {
    // optional instance-aware helpers
    getDetailByIref,
    hydrateDetailWithCatalogs,
    productsOf,              // promotion-flavored accessor (optional override)
    combosOf,                // promotion-flavored accessor (optional override)
    normalizeProductItem,    // promotion-flavored normalizer (optional override)
    normalizeComboItem,      // promotion-flavored normalizer (optional override)
    // fallbacks from this module
    toCurrencyName: _toCurrencyName = toCurrencyName,
    csvCell: _csvCell = csvCell,
    downloadBlob: _downloadBlob = downloadBlob,
    todayISO: _todayISO = todayISO,
  } = helpers;

  // --- internal defaults that call this module’s PROMO helpers ---
  const _products = (d) => (productsOf ? productsOf(d) : promoProductsOf(d));
  const _combos   = (d) => (combosOf   ? combosOf(d)   : promoCombosOf(d));
  const _normP    = (it, d, i) => (normalizeProductItem ? normalizeProductItem(it, d, i)
                                                        : normalizePromotionProductItem(it, d, i));
  const _normC    = (it, d, i) => (normalizeComboItem   ? normalizeComboItem(it, d, i)
                                                        : normalizePromotionComboItem(it, d, i));
  const _hydrate  = (d) => (hydrateDetailWithCatalogs ? hydrateDetailWithCatalogs(d) : d);
  const _getDet   = (id) => (getDetailByIref ? getDetailByIref(id) : null);

  if (!withDetails) {
    // --- summary CSV (one line per list) ---
    const header = ['company','location','version','currency','withVAT','dateFrom','dateTo','products','combos'];
    const lines = (lists || []).map((r)=> [
      (r.company?.name || r.company || ''),
      r.location?.name || r.location,
      r.version,
      _toCurrencyName(r.currency),
      r.withVAT ? 'withVAT' : 'noVAT',
      r.dateFrom || '',
      r.dateTo || '',
      (r.productCount ?? r._prodCount ?? 0),
      (r.comboCount   ?? r._comboCount ?? 0),
    ].map(_csvCell).join(','));
    const csv = [header.join(','), ...lines].join('\n');
    _downloadBlob(csv, `latest_promotions_${_todayISO()}.csv`, 'text/csv;charset=utf-8');
    return;
  }

  // --- detailed CSV (meta block + lines for each list) ---
  const rows = [];
  (lists || []).forEach((r) => {
    let det = r;
    const hasItems = !!(det && (det.productPromotion_list || det.comboPromotion_list));
    if (!hasItems && _getDet) {
      const maybe = _getDet(r.iref);
      // don't await promises here; if a promise is returned, fallback to thin row
      det = (maybe && typeof maybe.then === 'function') ? r : (maybe || r);
    }
    if (det) det = _hydrate(det);

    // Meta header (Promotion)
    [
      ['Promotion List'],
      ['Location', (det?.location?.name || det?.location || '')],
      ['Version', String(det?.version || '')],
      ['Currency', _toCurrencyName(det?.currency)],
      ['VAT', det?.withVAT ? 'With VAT' : 'No VAT'],
      ['From', det?.dateFrom || ''],
      ['To', det?.dateTo || '∞'],
      ['Description', det?.description || '']
    ].forEach(line => rows.push(line.map(_csvCell).join(',')));

    rows.push('');
    rows.push(['Type','Iref','Name','SKU','Unit','Class','Category','Price','Currency','Active','IsForSale','Status'].join(','));

    // Products
    (_products(det) || []).forEach((raw, i) => {
      const item = _normP(raw, det, i) || raw || {};
      const name = item.name
        || raw.name
        || item.product?.name
        || item.productRef?.name
        || '';
      const sku  = item.sku
        || raw.sku
        || item.product?.sku
        || item.productRef?.sku
        || '';
      const value = (item.price != null)
        ? item.price
        : (raw.price != null ? raw.price : (raw.value != null ? raw.value : ''));
      const unit  = item.unitType?.name || item.unitType || '';
      const cls   = item.pCategory?.name || '';
      const cat   = item.pSubCategory?.name || '';
      const curr  = item.currency || raw.currency || det?.currency;

      rows.push([
        'Product',
        item.product?.iref || item.productRef?.iref || '',
        name,
        sku,
        unit,
        cls,
        cat,
        value,
        _toCurrencyName(curr),
        item.isActive ? '1' : '0',
        item.isForSale ? '1' : '0',
        item.status || ''
      ].map(_csvCell).join(','));
    });

    // Combos
    (_combos(det) || []).forEach((raw, i) => {
      const item = _normC(raw, det, i) || raw || {};
      const name = item.name
        || raw.name
        || item.productCombo?.name
        || item.comboRef?.name
        || '';
      const sku  = item.sku
        || raw.sku
        || item.productCombo?.sku
        || item.comboRef?.sku
        || '';
      const value = (item.price != null)
        ? item.price
        : (raw.price != null ? raw.price : (raw.value != null ? raw.value : ''));
      const curr  = item.currency || raw.currency || det?.currency;

      rows.push([
        'Combo',
        item.productCombo?.iref || item.comboRef?.iref || '',
        name,
        sku,
        '', '', '',
        value,
        _toCurrencyName(curr),
        item.isActive ? '1' : '0',
        item.isForSale ? '1' : '0',
        item.status || ''
      ].map(_csvCell).join(','));
    });

    rows.push(''); // spacer between lists
  });

  const csv = rows.join('\n');
  _downloadBlob(csv, `promotions_detailed_${_todayISO()}.csv`, 'text/csv;charset=utf-8');
}

/* ---------- COMPANY-specific helpers (non-breaking) ---------- */

// Arrays from Company list detail
export const companyProductsOf = (detail) => {
  const raw = detail?.productCompany_list || [];
  return Array.isArray(raw) ? raw : Object.values(raw || {});
};
export const companyCombosOf = (detail) => {
  const raw = detail?.comboCompany_list || [];
  return Array.isArray(raw) ? raw : Object.values(raw || {});
};

// Count _prodCount / _comboCount on company lists
export function enrichCompanyCounts(pl) {
  const prod = Array.isArray(pl?.productCompany_list) ? pl.productCompany_list
             : (pl?.productCompany_list ? Object.values(pl.productCompany_list) : []);
  const comb = Array.isArray(pl?.comboCompany_list) ? pl.comboCompany_list
             : (pl?.comboCompany_list ? Object.values(pl.comboCompany_list) : []);
  return {
    ...pl,
    _prodCount: prod.length,
    _comboCount: comb.length,
  };
}

// Normalize to a shared shape used by CSV/UI (map `value` -> `price`)
export const normalizeCompanyProductItem = (it, parent, idx) => {
  const name = it?.name || it?.product?.name || it?.productRef?.name || '';
  const sku  = it?.sku  || it?.product?.sku  || it?.productRef?.sku  || '';
  return {
    ...it,
    name,
    sku,
    price: (it?.price != null ? it.price : it?.value),
    currency: it?.currency ?? parent?.currency,
  };
};
export const normalizeCompanyComboItem = (it, parent, idx) => {
  const name = it?.name || it?.productCombo?.name || it?.comboRef?.name || '';
  const sku  = it?.sku  || it?.productCombo?.sku  || it?.comboRef?.sku  || '';
  return {
    ...it,
    name,
    sku,
    price: (it?.price != null ? it.price : it?.value),
    currency: it?.currency ?? parent?.currency,
  };
};

/* ---------- CSV: single company list (detail) ---------- */
export function exportCompanyDetailCSV(detail, helpers = {}) {
  if (!detail) return;
  const {
    toCurrencyName: _toCurrencyName = toCurrencyName,
    csvCell: _csvCell = csvCell,
    downloadBlob: _downloadBlob = downloadBlob,
    productsOf = companyProductsOf,
    combosOf   = companyCombosOf,
    normalizeProductItem = normalizeCompanyProductItem,
    normalizeComboItem   = normalizeCompanyComboItem,
  } = helpers;

  const rows = [];
  [
    ['Company List'],
    ['Company', (detail.company?.name || detail.company || '')],
    ['Location', (detail.location?.name || detail.location || '')],
    ['Version', String(detail.version || '')],
    ['Currency', _toCurrencyName(detail.currency)],
    ['VAT', detail.withVAT ? 'With VAT' : 'No VAT'],
    ['From', detail.dateFrom || ''],
    ['To', detail.dateTo || '∞'],
    ['Description', detail.description || '']
  ].forEach(line => rows.push(line.map(_csvCell).join(',')));

  rows.push('');
  rows.push(['Type','Iref','Name','SKU','Unit','Class','Category','Price','Currency','Active','IsForSale','Status'].join(','));

  (productsOf(detail) || []).forEach((raw,i)=>{
    const item = (helpers.normalizeProductItem || normalizeProductItem)(raw, detail, i) || {};
    rows.push([
      'Product',
      item.product?.iref || item.productRef?.iref || '',
      item.name || '',
      item.sku || '',
      (item.unitType?.name || item.unitType || ''),
      (item.pCategory?.name || ''),
      (item.pSubCategory?.name || ''),
      (item.price != null ? item.price : ''),
      _toCurrencyName(item.currency || detail.currency),
      item.isActive ? '1' : '0',
      item.isForSale ? '1' : '0',
      item.status || ''
    ].map(_csvCell).join(','));
  });

  (combosOf(detail) || []).forEach((raw,i)=>{
    const item = (helpers.normalizeComboItem || normalizeComboItem)(raw, detail, i) || {};
    rows.push([
      'Combo',
      item.productCombo?.iref || item.comboRef?.iref || '',
      item.name || '',
      item.sku || '',
      '', '', '',
      (item.price != null ? item.price : ''),
      _toCurrencyName(item.currency || detail.currency),
      item.isActive ? '1' : '0',
      item.isForSale ? '1' : '0',
      item.status || ''
    ].map(_csvCell).join(','));
  });

  const csv = rows.join('\n');
  const fname = `companylist_${(detail.company?.name||detail.company)}_${(detail.location?.name||detail.location)}_v${detail.version}.csv`;
  _downloadBlob(csv, fname, 'text/csv;charset=utf-8');
}

/* ---------- CSV: many company lists (summary OR detailed) ---------- */
export function exportCompanyLatestCSV(lists, helpers = {}, options = {}) {
  const { withDetails = false } = options;
  const {
    toCurrencyName: _toCurrencyName = toCurrencyName,
    csvCell: _csvCell = csvCell,
    downloadBlob: _downloadBlob = downloadBlob,
    todayISO: _todayISO = todayISO,
    getDetailByIref,
    hydrateDetailWithCatalogs,
    productsOf = companyProductsOf,
    combosOf   = companyCombosOf,
    normalizeProductItem = normalizeCompanyProductItem,
    normalizeComboItem   = normalizeCompanyComboItem,
  } = helpers;

  const _hydrate = (d) => (hydrateDetailWithCatalogs ? hydrateDetailWithCatalogs(d) : d);
  const _getDet  = (id) => (getDetailByIref ? getDetailByIref(id) : null);

  if (!withDetails) {
    const header = ['company','location','version','currency','withVAT','dateFrom','dateTo','products','combos'];
    const lines = (lists || []).map((r)=> [
      (r.company?.name || r.company || ''),
      (r.location?.name || r.location || ''),
      r.version,
      _toCurrencyName(r.currency),
      r.withVAT ? 'withVAT' : 'noVAT',
      r.dateFrom || '',
      r.dateTo || '',
      (r.productCount ?? r._prodCount ?? 0),
      (r.comboCount   ?? r._comboCount ?? 0),
    ].map(_csvCell).join(','));
    const csv = [header.join(','), ...lines].join('\n');
    _downloadBlob(csv, `latest_companylists_${_todayISO()}.csv`, 'text/csv;charset=utf-8');
    return;
  }

  // detailed: meta block + items for each list
  const rows = [];
  (lists || []).forEach((r) => {
    let det = r;
    const hasItems = !!(det && (det.productCompany_list || det.comboCompany_list));
    if (!hasItems && _getDet) {
      const maybe = _getDet(r.iref);
      det = (maybe && typeof maybe.then === 'function') ? r : (maybe || r);
    }
    if (det) det = _hydrate(det);

    // meta header
    [
      ['Company List'],
      ['Company', (det?.company?.name || det?.company || '')],
      ['Location', (det?.location?.name || det?.location || '')],
      ['Version', String(det?.version || '')],
      ['Currency', _toCurrencyName(det?.currency)],
      ['VAT', det?.withVAT ? 'With VAT' : 'No VAT'],
      ['From', det?.dateFrom || ''],
      ['To', det?.dateTo || '∞'],
      ['Description', det?.description || '']
    ].forEach(line => rows.push(line.map(_csvCell).join(',')));

    rows.push('');
    rows.push(['Type','Iref','Name','SKU','Unit','Class','Category','Price','Currency','Active','IsForSale','Status'].join(','));

    (productsOf(det) || []).forEach((raw,i)=>{
      const item = (helpers.normalizeProductItem || normalizeProductItem)(raw, det, i) || {};
      rows.push([
        'Product', item.product?.iref || item.productRef?.iref || '', item.name || '', item.sku || '',
        (item.unitType?.name || item.unitType || ''),
        (item.pCategory?.name || ''), (item.pSubCategory?.name || ''),
        (item.price != null ? item.price : ''), _toCurrencyName(item.currency || det.currency),
        item.isActive ? '1' : '0', item.isForSale ? '1' : '0',item.status || ''
      ].map(_csvCell).join(','));
    });

    (combosOf(det) || []).forEach((raw,i)=>{
      const item = (helpers.normalizeComboItem || normalizeComboItem)(raw, det, i) || {};
      rows.push([
        'Combo', item.productCombo?.iref || item.comboRef?.iref || '', item.name || '', item.sku || '', '', '', '',
        (item.price != null ? item.price : ''), _toCurrencyName(item.currency || det.currency),
        item.isActive ? '1' : '0', item.isForSale ? '1' : '0',item.status || ''
      ].map(_csvCell).join(','));
    });

    rows.push('');
  });

  const csv = rows.join('\n');
  _downloadBlob(csv, `companylists_detailed_${_todayISO()}.csv`, 'text/csv;charset=utf-8');
}

// --- ACQUISITION-specific helpers (add to bottom of ListsShared.js) ---

export const latestKeyAcq = (x) =>
  `${x?.company?.iref || x?.company}__${x?.currency}__${x?.withVAT ? 'VAT' : 'NOVAT'}`;


export const acqProductsOf = (detail) => {
  const raw = detail?.productAcquisition_list || [];
  return Array.isArray(raw) ? raw : Object.values(raw || {});
};

export function enrichAcquisitionCounts(pl){
  const prod = Array.isArray(pl?.productAcquisition_list) ? pl.productAcquisition_list
             : (pl?.productAcquisition_list ? Object.values(pl.productAcquisition_list) : []);
  return { ...pl, _prodCount: prod.length };
}

// Normalize Acquisition product lines (value -> price)
export const normalizeAcqProductItem = (it, parent, idx) => {
  const name = it?.name || it?.product?.name || it?.productRef?.name || '';
  const sku  = it?.sku  || it?.product?.sku  || it?.productRef?.sku  || '';
  return {
    ...it,
    name,
    sku,
    price: (it?.price != null ? it.price : it?.value),
    currency: it?.currency ?? parent?.currency,
  };
};

// CSV: single acquisition list (detail)
export function exportAcquisitionDetailCSV(detail, helpers = {}) {
  if (!detail) return;
  const {
    toCurrencyName: _toCurrencyName = toCurrencyName,
    csvCell: _csvCell = csvCell,
    downloadBlob: _downloadBlob = downloadBlob,
    productsOf = acqProductsOf,
    normalizeProductItem = normalizeAcqProductItem,
  } = helpers;

  const rows = [];
  [
    ['Acquisition List'],
    ['Company', (detail.company?.name || detail.company || '')],
    ['Version', String(detail.version || '')],
    ['Currency', _toCurrencyName(detail.currency)],
    ['VAT', detail.withVAT ? 'With VAT' : 'No VAT'],
    ['From', detail.dateFrom || ''],
    ['To', detail.dateTo || '∞'],
    ['Description', detail.description || '']
  ].forEach(line => rows.push(line.map(_csvCell).join(',')));

  rows.push('');
  rows.push(['Type','Name','SKU','Unit','Class','Category','Price','Currency','Active','Purchased','Status'].join(','));

  (productsOf(detail) || []).forEach((raw,i)=>{
    const item = (helpers.normalizeProductItem || normalizeProductItem)(raw, detail, i) || {};
    rows.push([
      'Product',
      item.product?.iref || item.productRef?.iref || '',
      item.name || '',
      item.sku || '',
      (item.unitType?.name || item.unitType || ''),
      (item.pCategory?.name || ''),
      (item.pSubCategory?.name || ''),
      (item.price != null ? item.price : ''),
      _toCurrencyName(item.currency || detail.currency),
      item.isActive ? '1' : '0', item.isPurchased ? '1' : '0', item.status || ''
    ].map(_csvCell).join(','));
  });

  const csv = rows.join('\n');
  const fname = `acquisition_${(detail.company?.name||detail.company)}_v${detail.version}.csv`;
  _downloadBlob(csv, fname, 'text/csv;charset=utf-8');
}

// CSV: many acquisition lists (detailed export)
export function exportAcquisitionLatestCSV(lists, helpers = {}, options = {}){
  const { withDetails = true } = options; // for Acquisition we always export detail
  const {
    getDetailByIref,
    hydrateDetailWithCatalogs,
    toCurrencyName: _toCurrencyName = toCurrencyName,
    csvCell: _csvCell = csvCell,
    downloadBlob: _downloadBlob = downloadBlob,
    todayISO: _todayISO = todayISO,
    productsOf = acqProductsOf,
    normalizeProductItem = normalizeAcqProductItem,
  } = helpers;

  const _hydrate = (d) => (hydrateDetailWithCatalogs ? hydrateDetailWithCatalogs(d) : d);
  const _getDet  = (id) => (getDetailByIref ? getDetailByIref(id) : null);

  if (!withDetails){
    const header = ['company','version','currency','withVAT','dateFrom','dateTo','products'];
    const lines = (lists||[]).map(r=>[
      (r.company?.name || r.company || ''),
      r.version,
      _toCurrencyName(r.currency),
      r.withVAT ? 'withVAT' : 'noVAT',
      r.dateFrom || '',
      r.dateTo || '',
      (r.productCount ?? r._prodCount ?? 0),
    ].map(_csvCell).join(','));
    const csv = [header.join(','), ...lines].join('\n');
    _downloadBlob(csv, `latest_acquisitions_${_todayISO()}.csv`, 'text/csv;charset=utf-8');
    return;
  }

  const rows = [];
  (lists || []).forEach((r) => {
    let det = r;
    const hasItems = !!(det && det.productAcquisition_list);
    if (!hasItems && _getDet) {
      const maybe = _getDet(r.iref);
      det = (maybe && typeof maybe.then === 'function') ? r : (maybe || r);
    }
    if (det) det = _hydrate(det);

    [
      ['Acquisition List'],
      ['Company', (det?.company?.name || det?.company || '')],
      ['Version', String(det?.version || '')],
      ['Currency', _toCurrencyName(det?.currency)],
      ['VAT', det?.withVAT ? 'With VAT' : 'No VAT'],
      ['From', det?.dateFrom || ''],
      ['To', det?.dateTo || '∞'],
      ['Description', det?.description || '']
    ].forEach(line => rows.push(line.map(_csvCell).join(',')));

    rows.push('');
    rows.push(['Type','Name','SKU','Unit','Class','Category','Price','Currency','Active','Purchased','Status'].join(','));

    (productsOf(det) || []).forEach((raw,i)=>{
      const item = (normalizeProductItem)(raw, det, i) || {};
      rows.push([
        'Product',
        item.product?.iref || item.productRef?.iref || '',
        item.name || '',
        item.sku || '',
        (item.unitType?.name || item.unitType || ''),
        (item.pCategory?.name || ''),
        (item.pSubCategory?.name || ''),
        (item.price != null ? item.price : ''),
        _toCurrencyName(item.currency || det.currency),
        item.isActive ? '1' : '0', item.isPurchased ? '1' : '0', item.status || ''
      ].map(_csvCell).join(','));
    });

    rows.push('');
  });

  const csv = rows.join('\n');
  _downloadBlob(csv, `acquisitions_detailed_${_todayISO()}.csv`, 'text/csv;charset=utf-8');
}
